import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from './../../../environments/environment';
import { DEFAULT_SEND_WINDOWS, SendWindow, SendWindows } from 'src/app/models/SendWindow';

/**
 * `GET /send-windows` (M8 / CU-17tm466nzee) — janelas de SMS Massivo e de
 * ligação, para `app-send-when`/`app-window-datetime-picker` montarem a
 * grade sem hardcodar os limites. O backend continua sendo a trava real
 * (`AppHelper::broadcastCanSend`, `CallSendWindow::isBlocked`); isto é só
 * para a UI não ficar sem grade.
 *
 * Resposta inválida ou erro (rede fora; o stub do Cypress em
 * `carteira-stub.js` intercepta `**\/api/**` com corpo `{}`) caem no
 * `DEFAULT_SEND_WINDOWS`, nunca propagam erro para quem assina.
 */
@Injectable({ providedIn: 'root' })
export class SendWindowsService {

  private cached$: Observable<SendWindows> | null = null;

  constructor(private http: HttpClient) { }

  get(): Observable<SendWindows> {
    if (!this.cached$) {
      this.cached$ = this.http.get<SendWindows>(`${environment.apiUrl}/send-windows`).pipe(
        map(response => this.normalize(response)),
        catchError(() => of(DEFAULT_SEND_WINDOWS)),
        shareReplay(1),
      );
    }

    return this.cached$;
  }

  private normalize(response: unknown): SendWindows {
    const raw = (response ?? {}) as Partial<SendWindows>;
    const sms = this.normalizeWindow(raw.sms, DEFAULT_SEND_WINDOWS.sms);
    const call = this.normalizeWindow(raw.call, DEFAULT_SEND_WINDOWS.call);

    return {
      timezone: typeof raw.timezone === 'string' && raw.timezone ? raw.timezone : DEFAULT_SEND_WINDOWS.timezone,
      now: typeof raw.now === 'string' && raw.now ? raw.now : new Date().toISOString(),
      sms,
      call,
    };
  }

  private normalizeWindow(window: Partial<SendWindow> | undefined, fallback: SendWindow): SendWindow {
    const isValidTime = (value: unknown): value is string => typeof value === 'string' && /^\d{2}:\d{2}$/.test(value);

    if (!window || !isValidTime(window.start) || !isValidTime(window.stop) || window.start >= window.stop) {
      return fallback;
    }

    return { start: window.start, stop: window.stop };
  }
}
